/**
 * CitedFieldRow
 *
 * A field row component that displays a value with inline citation pills.
 * When clicked, it triggers selection for showing AI explanation in the right pane.
 * Citations appear as compact, clickable pills next to the value.
 */
import {
  ArrowTopRightOnSquareIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { InspectArtifactModal } from '@/components/artifact-inspector/InspectArtifactModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface CitationSource {
  /** ID of the artifact this citation comes from */
  artifact_id: string | null;
  /** Citation IDs within the artifact */
  citation_ids: string[];
  /** Document name for display */
  document_name?: string;
}

export interface CitedFieldRowProps {
  id: string;
  label: string;
  value: string;
  /** Whether this field is required */
  required?: boolean;
  /** Citation sources for this field */
  citations?: CitationSource[];
  /** Whether this row is currently selected for showing explanation */
  isSelected?: boolean;
  /** Callback when field is clicked/selected */
  onSelect?: () => void;
  /** Whether AI extracted this value */
  isAIExtracted?: boolean;
  /** Confidence score (0-1) */
  confidence?: number | null;
  /** Allow inline editing */
  editable?: boolean;
  /** Callback when value changes */
  onChange?: (value: string) => void;
  /** Callback when save is triggered */
  onSave?: () => void;
  /** Whether save is in progress */
  isSaving?: boolean;
}

/**
 * Inline citation pill that opens the artifact inspector
 */
const CitationPill: React.FC<{
  citation: CitationSource;
  index: number;
}> = ({ citation, index }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!citation.artifact_id) return null;

  const label =
    citation.document_name ||
    `Source ${index + 1}` ||
    `Doc ${citation.artifact_id.slice(-6)}`;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsModalOpen(true);
        }}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-cyan-200 dark:text-cyan-900 dark:hover:bg-cyan-100 dark:border-cyan-300 rounded-full transition-colors border border-blue-200/50"
        title={`View source: ${label}`}
      >
        <ArrowTopRightOnSquareIcon className="h-2.5 w-2.5" />
        <span className="max-w-[80px] truncate">{label}</span>
        {citation.citation_ids.length > 1 && (
          <span className="text-blue-500">
            ({citation.citation_ids.length})
          </span>
        )}
      </button>
      <InspectArtifactModal
        artifactId={citation.artifact_id}
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
      />
    </>
  );
};

/**
 * Confidence indicator badge
 */
const ConfidenceBadge: React.FC<{ confidence: number }> = ({ confidence }) => {
  const percentage = Math.round(confidence * 100);
  const colorClass =
    confidence > 0.8
      ? 'bg-green-50 text-green-700 border-green-200'
      : confidence > 0.6
        ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
        : 'bg-red-50 text-red-700 border-red-200';

  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${colorClass}`}
    >
      {percentage}%
    </span>
  );
};

export const CitedFieldRow: React.FC<CitedFieldRowProps> = ({
  id,
  label,
  value,
  required = false,
  citations = [],
  isSelected = false,
  onSelect,
  isAIExtracted = false,
  confidence,
  editable = false,
  onChange,
  onSave,
  isSaving = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(value);

  const hasCitations = citations.length > 0;
  const hasValue = Boolean(value?.trim());

  const handleClick = () => {
    if (!isEditing) {
      onSelect?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isEditing) {
      onSave?.();
      setIsEditing(false);
    }
    if (e.key === 'Escape') {
      setDraftValue(value);
      setIsEditing(false);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (!isEditing && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect?.();
        }
      }}
      className={`
        group px-4 py-3 rounded-lg transition-all cursor-pointer
        ${isSelected ? 'bg-indigo-50 ring-2 ring-indigo-300 shadow-sm' : 'hover:bg-gray-50'}
        ${!hasValue && required ? 'border-l-2 border-red-300' : ''}
      `}
      aria-label={`${label}${required ? ' (required)' : ''}`}
    >
      {/* Header row: Label + badges */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          {required && !hasValue && (
            <span className="text-[10px] uppercase tracking-wide text-red-500 font-medium">
              Required
            </span>
          )}
          {!required && (
            <span className="text-[10px] uppercase tracking-wide text-gray-400">
              Optional
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isAIExtracted && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-700 rounded-full border border-purple-200/50">
                  <SparklesIcon className="h-2.5 w-2.5 mr-0.5" />
                  AI
                </span>
              </TooltipTrigger>
              <TooltipContent>Extracted by AI</TooltipContent>
            </Tooltip>
          )}
          {confidence !== null && confidence !== undefined && (
            <ConfidenceBadge confidence={confidence} />
          )}
        </div>
      </div>

      {/* Value row with inline citations */}
      <div className="flex items-start gap-2">
        {isEditing ? (
          <div className="flex-1 flex items-center gap-2">
            <Input
              id={id}
              value={draftValue}
              onChange={(e) => {
                setDraftValue(e.target.value);
                onChange?.(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              className="h-8 text-sm"
              disabled={isSaving}
              autoFocus
            />
            <Button
              size="sm"
              variant="ghost"
              className="text-xs h-7"
              onClick={() => {
                setDraftValue(value);
                setIsEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="text-xs h-7"
              onClick={() => {
                onSave?.();
                setIsEditing(false);
              }}
              disabled={isSaving}
            >
              Save
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <span
                className={`text-sm ${
                  hasValue ? 'text-gray-900' : 'text-gray-400 italic'
                }`}
              >
                {value || '—'}
              </span>
            </div>

            {/* Inline citation pills */}
            {hasCitations && (
              <div className="flex items-center gap-1 flex-wrap">
                {citations.slice(0, 3).map((citation, idx) => (
                  <CitationPill
                    key={citation.artifact_id || idx}
                    citation={citation}
                    index={idx}
                  />
                ))}
                {citations.length > 3 && (
                  <span className="text-[10px] text-gray-500">
                    +{citations.length - 3} more
                  </span>
                )}
              </div>
            )}

            {/* Edit button on hover */}
            {editable && (
              <Button
                size="sm"
                variant="ghost"
                className="opacity-0 group-hover:opacity-100 transition-opacity text-xs h-7"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
              >
                Edit
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
